/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetDistributionPluginRequest,
  GetDistributionPluginSecurity,
} from "../models/operations/getdistributionplugin.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDistributionPluginQuery,
  DistributionPluginQueryData,
  prefetchDistributionPlugin,
  queryKeyDistributionPlugin,
} from "./distributionPlugin.core.js";
export {
  buildDistributionPluginQuery,
  type DistributionPluginQueryData,
  prefetchDistributionPlugin,
  queryKeyDistributionPlugin,
};

export type DistributionPluginQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getDistributionPlugin plugins
 *
 * @remarks
 * Get minimal distribution target metadata for a skill the caller can read. Requires skill:read, not org:read.
 */
export function useDistributionPlugin(
  request: GetDistributionPluginRequest,
  security?: GetDistributionPluginSecurity | undefined,
  options?: QueryHookOptions<
    DistributionPluginQueryData,
    DistributionPluginQueryError
  >,
): UseQueryResult<DistributionPluginQueryData, DistributionPluginQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildDistributionPluginQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getDistributionPlugin plugins
 *
 * @remarks
 * Get minimal distribution target metadata for a skill the caller can read. Requires skill:read, not org:read.
 */
export function useDistributionPluginSuspense(
  request: GetDistributionPluginRequest,
  security?: GetDistributionPluginSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DistributionPluginQueryData,
    DistributionPluginQueryError
  >,
): UseSuspenseQueryResult<
  DistributionPluginQueryData,
  DistributionPluginQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDistributionPluginQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDistributionPluginData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      skillId: string;
      id: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: DistributionPluginQueryData,
): DistributionPluginQueryData | undefined {
  const key = queryKeyDistributionPlugin(...queryKeyBase);

  return client.setQueryData<DistributionPluginQueryData>(key, data);
}

export function invalidateDistributionPlugin(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      skillId: string;
      id: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "plugins",
      "getDistributionPlugin",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDistributionPlugin(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "plugins", "getDistributionPlugin"],
  });
}
